import React, { useEffect, useState } from 'react';
import {
  Trash, Info, X, Check, ArrowClockwise, ShieldCheck,
} from '@phosphor-icons/react';
import SearchableSelect from './SearchableSelect';
import SectionLock from './SectionLock';
import {
  getBranchNames, getGroupCode, getTickets, getRemovedChitLiveDetails,
  deleteRemovedChitLive, saveData,
} from '../api/connectionRegistryApi';
import { useLoginName, apiErrorMessage } from '../utils/auditUser';
import { NO_DATA_MESSAGE, NO_DATA_SUMMARY, isEmptyResult } from '../utils/noData';
import Toast from './Toast';
import { useToast } from '../utils/useToast';

const CHANGE_TYPE = 'REMOVED_CHIT_LIVE_DELETE';

const TH = { padding: '10px 12px', fontSize: '11px', fontWeight: '600', color: '#6B7280', whiteSpace: 'nowrap', textAlign: 'left' };
const TD = { padding: '10px 12px', fontSize: '13px', color: '#111827', whiteSpace: 'nowrap' };

// The grid endpoint is not final, so each cell accepts the camelCase or snake_case spelling.
const pick = (row, ...keys) => {
  for (const k of keys) {
    if (row[k] !== undefined && row[k] !== null && row[k] !== '') return row[k];
  }
  return '';
};

const toRow = (r) => ({
  subscriberName: pick(r, 'subscriberName', 'subscriber_name'),
  subscriberBranchName: pick(r, 'subscriberBranchName', 'subscriber_branch_name', 'branch_name'),
  groupCode: pick(r, 'groupCode', 'groupcode', 'group_Code'),
  ticketNo: pick(r, 'ticketNo', 'ticketno'),
  removedDate: pick(r, 'removedDate', 'removed_date', 'removaldate', 'removal_date'),
  installmentNo: pick(r, 'installmentNo', 'installment_no', 'installmentno'),
});

function RemovedChitLive() {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);

  const [branchlist, setBranchlist] = useState([]);
  const [groups, setGroups] = useState([]);
  const [tickets, setTickets] = useState([]);

  const [branch, setBranch] = useState('');
  const [group, setGroup] = useState('');
  const [ticket, setTicket] = useState('');

  const [branchschema, setBranchschema] = useState(null);
  const [branchId, setBranchId] = useState(null);
  const [groupCode, setGroupCode] = useState(null);
  const [chitgroupId, setChitgroupId] = useState(null);

  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast, setToast, showToast } = useToast();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    getBranchNames()
      .then((res) => setBranchlist(Array.isArray(res) ? res : []))
      .catch(() => showToast('error', 'Load Failed', 'Failed to load branches. Please try again.'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  const resetFromTicket = () => {
    setRows([]);
    setLoaded(false);
  };

  const resetFromGroup = () => {
    setGroupCode(null);
    setChitgroupId(null);
    setTicket('');
    setTickets([]);
    resetFromTicket();
  };

  const resetFromBranch = () => {
    setBranchschema(null);
    setBranchId(null);
    setGroup('');
    setGroups([]);
    resetFromGroup();
  };

  const handleBranchChange = async (value) => {
    setBranch(value || '');
    resetFromBranch();
    if (!value) return;

    const selected = branchlist.find((x) => x.branch_name === value);
    if (!selected) return;

    setBranchschema(selected.branch_code);
    setBranchId(selected.tbl_mst_branch_configuration_id);

    try {
      const res = await getGroupCode(selected.branch_code);
      const list = Array.isArray(res) ? res : [];
      setGroups(list);
      if (list.length === 0) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setGroups([]);
      showToast('error', 'Load Failed', 'Failed to load group codes. Please try again.');
    }
  };

  const handleGroupChange = async (value) => {
    setGroup(value || '');
    resetFromGroup();
    if (!value) return;

    const selected = groups.find((x) => x.group_Code === value);
    if (!selected) return;

    setGroupCode(selected.group_Code);
    setChitgroupId(selected.chitgroup_id);

    try {
      const res = await getTickets(branchschema, selected.group_Code);
      const list = Array.isArray(res) ? res : [];
      setTickets(list);
      if (list.length === 0) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setTickets([]);
      showToast('error', 'Load Failed', 'Failed to load tickets. Please try again.');
    }
  };

  const handleTicketChange = async (value) => {
    const empty = value === '' || value === null || value === undefined;
    setTicket(empty ? '' : value);
    resetFromTicket();
    if (empty) return;

    try {
      const res = await getRemovedChitLiveDetails(branchschema, groupCode, value);
      const list = (Array.isArray(res) ? res : []).map(toRow);
      setRows(list);
      setLoaded(true);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch (err) {
      console.error('[getRemovedChitLiveDetails] error =', err);
      setRows([]);
      showToast('error', 'Load Failed', apiErrorMessage(err, 'Failed to load removed chit details.'));
    }
  };

  const branchInvalid = touched && !branch;
  const groupInvalid = touched && !group;
  const ticketInvalid = touched && ticket === '';
  const reasonInvalid = touched && (reason.trim().length < 10 || reason.length > 200);

  const isValid = !!branch && !!group && ticket !== ''
    && reason.trim().length >= 10 && reason.length <= 200;

  const handleDelete = () => {
    setTouched(true);
    if (!isValid) {
      showToast('warn', 'Incomplete Form', 'Please fill all required fields correctly.');
      return;
    }
    if (rows.length === 0) {
      showToast('warn', NO_DATA_SUMMARY, 'No removed chit record is loaded for the selected ticket.');
      return;
    }
    setShowConfirmModal(true);
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setBranch('');
    resetFromBranch();
    setReason('');
    setTouched(false);
    setShowConfirmModal(false);
  };

  const cancelConfirm = () => {
    if (saving) return;
    setShowConfirmModal(false);
  };

  // Audit first: the delete is not attempted when the audit entry cannot be saved.
  const confirmDelete = async () => {
    setSaving(true);

    const audit = {
      schemaName: branchschema,
      loginName,
      loginTime: new Date().toISOString(),
      changeType: CHANGE_TYPE,
      oldData: {
        branchName: branch, branchSchema: branchschema, groupCode, ticketNo: ticket, rows,
      },
      newData: { status: 'DELETED' },
      reason: reason.trim(),
    };

    try {
      await saveData([audit]);
    } catch (err) {
      setSaving(false);
      setShowConfirmModal(false);
      showToast('error', 'Audit Save Failed', apiErrorMessage(err, 'Audit save failed. Delete was not performed.'), true);
      return;
    }

    try {
      const result = await deleteRemovedChitLive({
        branchSchema: branchschema,
        branchId: Number(branchId),
        chitGroupId: Number(chitgroupId),
        groupCode,
        ticketNo: Number(ticket),
        doneBy: loginName,
      });
      const text = typeof result === 'string' ? result : JSON.stringify(result ?? '');
      setSaving(false);
      setShowConfirmModal(false);
      if (text.toLowerCase().includes('success') || text.toLowerCase().includes('deleted')) {
        showToast('success', 'Success', 'Audit saved and removed chit deleted successfully.', true);
        handleClear();
      } else {
        showToast('warn', 'Partial Success', `Audit saved, but delete failed: ${text || 'unknown error'}`, true);
      }
    } catch (err) {
      setSaving(false);
      setShowConfirmModal(false);
      showToast('warn', 'Partial Success', apiErrorMessage(err, 'Audit saved, but delete failed. Please try again.'), true);
    }
  };

  const lbl = { display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' };

  return (
    <div className="nc-page" style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="removed-chit-live-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="removed-chit-live-form">
        {saving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Trash size={18} color="#002FA7" />
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Chit Administration</div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>Removed Chit Live</div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Select Ticket</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose branch, group code and ticket to load the removed chit</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={lbl}>Branch Name <span style={{ color: '#DC2626' }}>*</span></label>
            <SearchableSelect
              key={`branch-${formKey}`}
              options={branchlist}
              getLabel={(o) => o.branch_name}
              getValue={(o) => o.branch_name}
              value={branch}
              onChange={handleBranchChange}
              placeholder="Select branch"
              emptyMessage="No branches found"
              error={branchInvalid}
              testId="removed-chit-live-branch"
            />
            {branchInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Branch Name is required</div>}
          </div>

          <div>
            <label style={lbl}>Group Code <span style={{ color: '#DC2626' }}>*</span></label>
            <SearchableSelect
              key={`group-${formKey}`}
              options={groups}
              getLabel={(o) => o.group_Code}
              getValue={(o) => o.group_Code}
              value={group}
              onChange={handleGroupChange}
              placeholder="Select group code"
              emptyMessage="Select a branch first"
              disabled={!branchschema}
              error={groupInvalid}
              testId="removed-chit-live-group"
            />
            {groupInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Group Code is required</div>}
          </div>

          <div>
            <label style={lbl}>Ticket No. <span style={{ color: '#DC2626' }}>*</span></label>
            <SearchableSelect
              key={`ticket-${formKey}`}
              options={tickets}
              getLabel={(o) => String(o.ticketno)}
              getValue={(o) => o.ticketno}
              value={ticket}
              onChange={handleTicketChange}
              placeholder="Select ticket no"
              emptyMessage="Select a group code first"
              disabled={!groupCode}
              error={ticketInvalid}
              testId="removed-chit-live-ticket"
            />
            {ticketInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Ticket No. is required</div>}
          </div>
        </div>

        {ticket !== '' && loaded && (
          <div style={{ marginTop: '20px', border: '1px solid #E5E7EB', borderRadius: '8px', overflowX: 'auto' }} data-testid="removed-chit-live-grid">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: '#F9FAFB' }}>
                <tr>
                  <th style={TH}>Subscriber Name</th>
                  <th style={TH}>Subscriber Branch Name</th>
                  <th style={TH}>Group Code</th>
                  <th style={TH}>Ticket No.</th>
                  <th style={TH}>Removed Date</th>
                  <th style={TH}>Installment No.</th>
                  <th style={TH}>Done By</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={7} style={{ ...TD, textAlign: 'center', color: '#9CA3AF' }}>{NO_DATA_MESSAGE}</td></tr>
                ) : rows.map((r, i) => (
                  <tr key={`${r.groupCode}-${r.ticketNo}-${i}`} style={{ borderTop: '1px solid #F3F4F6' }}>
                    <td style={TD}>{r.subscriberName}</td>
                    <td style={TD}>{r.subscriberBranchName}</td>
                    <td style={TD}>{r.groupCode}</td>
                    <td style={TD}>{r.ticketNo}</td>
                    <td style={TD}>{r.removedDate}</td>
                    <td style={TD}>{r.installmentNo}</td>
                    <td style={TD}>{loginName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={ticket === '' || rows.length === 0} hint="Select a ticket with a removed chit record in section A first">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
            <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Reason</div>
              <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Minimum 10 characters — this will be recorded in the audit log</div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Reason <span style={{ color: '#DC2626' }}>*</span></label>
            <span style={{ fontSize: '11px', color: reason.trim().length >= 10 ? '#059669' : reason.length > 180 ? '#D97706' : '#9CA3AF' }}>
              {reason.length} / 200
            </span>
          </div>
          <textarea
            className="input-field"
            style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
            rows={3}
            maxLength={200}
            placeholder="Enter reason (min 10 characters)..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="removed-chit-live-reason"
          />
          {reasonInvalid && (
            <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>
              {reason.trim().length === 0 ? 'Reason is required' : 'Minimum 10 characters required'}
            </div>
          )}
        </SectionLock>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#9CA3AF' }}>
            <Info size={13} /> Fields marked with <strong>*</strong> are required
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={handleClear} className="btn-secondary" disabled={saving} data-testid="removed-chit-live-clear-button">
              <ArrowClockwise size={14} style={{ marginRight: '4px' }} /> Clear
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={saving}
              data-testid="removed-chit-live-delete-button"
              style={{
                padding: '8px 16px', fontSize: '13px', fontWeight: '600', color: 'white',
                background: saving ? '#F3A6A6' : '#DC2626', border: 'none', borderRadius: '6px',
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              <Trash size={14} style={{ marginRight: '4px' }} /> {saving ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>

      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '440px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="removed-chit-live-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <ShieldCheck size={18} color="#DC2626" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm Removed Chit Deletion</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You&apos;re about to permanently delete the removed chit record for this ticket. Please review before confirming.
              </p>
              <div style={{ marginBottom: '14px' }}>
                <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Branch / Group / Ticket</p>
                <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>{branch} / {group} / {ticket}</p>
              </div>
              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{reason.trim()}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={saving} className="btn-secondary" data-testid="removed-chit-live-confirm-cancel">
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={saving}
                data-testid="removed-chit-live-confirm-delete"
                style={{
                  padding: '8px 16px', fontSize: '13px', fontWeight: '600', color: 'white',
                  background: saving ? '#F3A6A6' : '#DC2626', border: 'none', borderRadius: '6px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                }}
              >
                <Check size={14} style={{ marginRight: '4px' }} /> Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RemovedChitLive;
